import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import {
  LayoutDashboard,
  Users,
  Building2,
  CreditCard,
  Shield,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
} from "lucide-react";

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const menuItems = [
    {
      name: "Visão Geral",
      path: "/admin/dashboard",
      icon: LayoutDashboard,
      permission: null,
    },
    {
      name: "Clientes",
      path: "/admin/clients",
      icon: Users,
      permission: "users.view",
    },
    {
      name: "Assinaturas",
      path: "/admin/subscriptions",
      icon: CreditCard,
      permission: "users.view",
    },
    {
      name: "Planos",
      path: "/admin/plans",
      icon: CreditCard,
      permission: "plans.view",
    },
    {
      name: "Integrações",
      path: "/admin/integrations",
      icon: Building2,
      permission: "tenants.view",
    },
    {
      name: "WhatsApp",
      path: "/admin/whatsapps",
      icon: Building2,
      permission: "tenants.view",
    },
    {
      name: "Logs",
      path: "/admin/logs",
      icon: BarChart3,
      permission: "analytics.view",
    },
    {
      name: "Suporte",
      path: "/admin/support",
      icon: Shield,
      permission: null,
    },
    {
      name: "Admins",
      path: "/admin/admins",
      icon: Shield,
      permission: "admins.view",
    },
  ];

  const filteredMenu = menuItems.filter((item) => {
    if (!item.permission) return true;
    if (admin?.role_id === "super_admin") return true;
    return admin?.permissions?.includes(item.permission);
  });

  return (
    <div className="min-h-screen bg-zinc-950 text-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-zinc-900 border-r border-zinc-800 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-800">
            <div>
              <h1 className="text-xl font-bold text-emerald-500">ConvertPix</h1>
              <p className="text-xs text-zinc-400">Admin Panel</p>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Admin info */}
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{admin?.name}</p>
                <p className="text-xs text-zinc-400 truncate">{admin?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredMenu.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "text-zinc-400 hover:bg-zinc-800 hover:text-white"
                  }`}
                >
                  <Icon size={20} />
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-zinc-800">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full text-left text-zinc-400 hover:bg-zinc-800 hover:text-white rounded-lg transition-colors"
            >
              <LogOut size={20} />
              <span className="text-sm font-medium">Sair</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-zinc-900/95 backdrop-blur border-b border-zinc-800">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <Menu size={24} />
            </button>

            <div className="flex-1 lg:flex-none">
              <h2 className="text-lg font-semibold">
                {filteredMenu.find((item) => item.path === location.pathname)?.name || "Admin"}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium">{admin?.name}</p>
                <p className="text-xs text-zinc-400 capitalize">
                  {admin?.role_id?.replace("_", " ")}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
